/**
 * Server-side attribution (WEB-903): an opaque `lead_id` is generated on
 * landing and must survive until a conversion event (email capture / purchase).
 *
 * The id travels ONLY in the URL and in memory — nothing is ever written to
 * the device, so the PECR consent trigger is never met. `AttributionProvider`
 * captures it from the URL on landing; this module mirrors it in a plain
 * variable so non-React code (e.g. the Stripe `return_url` builders) can read
 * it without prop-drilling.
 *
 * `appendLeadId` re-appends the id to outgoing internal URLs — both at
 * hard-navigation boundaries (the Stripe `return_url`) and, via `TrackedLink` /
 * `useTrackedRouter`, on every internal link and programmatic navigation, so a
 * reload anywhere in the funnel recaptures it from the URL.
 */
import type { UrlObject } from 'url'

import { isFirstPartyHostForLeadId } from './firstPartyUrl'

// Single source of truth for the param name.
export const LEAD_ID_PARAM = 'lead_id'

// In-memory only. Cleared on full page reload; AttributionProvider re-hydrates
// it from the URL on mount.
let storedLeadId: string | null = null

/** Stores the captured lead_id in memory. Ignores empty values (never clears). */
export function setStoredLeadId(value: string | null | undefined): void {
  // Client-only: the module global is shared across requests on the server, so
  // never write it there (would risk cross-session contamination under SSR).
  if (typeof window === 'undefined') return
  const trimmed = value?.trim()
  if (trimmed) storedLeadId = trimmed
}

/** Reads the in-memory lead_id, or null if none captured this session. */
export function getStoredLeadId(): string | null {
  return storedLeadId
}

/** Test-only: reset the in-memory store. */
export function resetStoredLeadId(): void {
  storedLeadId = null
}

/** Reads the lead_id from URL search params. Returns null when absent/empty. */
export function getLeadId(params: URLSearchParams | null | undefined): string | null {
  const value = params?.get(LEAD_ID_PARAM)?.trim()
  return value ? value : null
}

function appendToStringHref(url: string, leadId: string): string {
  if (!url || url.startsWith('#') || /^(mailto:|tel:)/i.test(url)) return url
  try {
    const base = typeof window !== 'undefined' ? window.location.origin : 'http://localhost'
    const isProtocolRelative = url.startsWith('//')
    const isAbsolute = /^https?:\/\//i.test(url) || isProtocolRelative
    const parsed = new URL(url, base)
    // Never carry the tracking id onto a third-party domain. The serving origin counts as ours,
    // so a stage/preview deployment still carries it on its own absolute URLs.
    if (isAbsolute && !isFirstPartyHostForLeadId(parsed.host)) return url
    parsed.searchParams.set(LEAD_ID_PARAM, leadId)
    if (!isAbsolute) return `${parsed.pathname}${parsed.search}${parsed.hash}`
    // Preserve the original form: protocol-relative stays protocol-relative.
    return isProtocolRelative ? `//${parsed.host}${parsed.pathname}${parsed.search}${parsed.hash}` : parsed.toString()
  } catch {
    return url
  }
}

function appendToObjectHref(href: UrlObject, leadId: string): UrlObject {
  // A UrlObject with any authority/scheme parts set may point off-site — never
  // append the id to those (host alone is enough to change the destination).
  if (href.host || href.hostname || href.protocol) return href
  if (typeof href.pathname === 'string' && /^([a-z][a-z0-9+.-]*:|\/\/)/i.test(href.pathname)) return href
  if (typeof href.pathname === 'string' && href.pathname.startsWith('#')) return href

  const { query } = href
  if (typeof query === 'string') {
    const params = new URLSearchParams(query)
    params.set(LEAD_ID_PARAM, leadId)
    return { ...href, query: params.toString() }
  }
  return { ...href, query: { ...query, [LEAD_ID_PARAM]: leadId } }
}

type Href = string | UrlObject

/**
 * Returns `href` with `lead_id` set on its query string, preserving the input
 * type (string or Next `UrlObject`) and the rest of the URL. Strings work for
 * both relative paths and absolute FIRST-PARTY URLs (e.g. the Stripe
 * `return_url`); `UrlObject`s must be internal (no host/protocol). No-op when
 * there is no lead_id, for third-party URLs, and for `mailto:` / `tel:` / bare
 * `#hash` targets. Defaults to the in-memory stored id.
 */
export function appendLeadId<T extends Href>(href: T, leadId: string | null = getStoredLeadId()): T {
  if (!leadId || !href) return href
  if (typeof href === 'string') return appendToStringHref(href, leadId) as T
  return appendToObjectHref(href, leadId) as T
}
